import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../utils/roles/client-roles';
import { Dispatch, SetStateAction, useState } from 'react';
import { ContactGroup, Person } from '../../../../../types';
import AddContactGroupDialog from './add-contactGroup-dialog';
import DeleteContactGroupDialog from './delete-contactGroup-dialog';
import EditContactGroupDialog from './edit-contactGroup-dialog';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Skeleton from '@mui/material/Skeleton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Add from '@mui/icons-material/Add';
import Google from '@mui/icons-material/Google';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Label from '@mui/icons-material/Label';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';

interface PersonalContactsLabelsProps {
  userEmail: string;
  userView: boolean;
  userRoles: string[];
  changeLogRoles: string[];
  contactGroups: ContactGroup[] | undefined;
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  setSelectedContact: Dispatch<SetStateAction<Person | null>>;
  selectedContactGroup: ContactGroup | null;
  setSelectedContactGroup: Dispatch<SetStateAction<ContactGroup | null>>;
  isLoadingLabels: boolean;
  selectedLabel: string;
  setSelectedLabel: Dispatch<SetStateAction<string>>;
}

export default function PersonalContactsLabels({
  userEmail,
  userView,
  userRoles,
  changeLogRoles,
  contactGroups,
  setSelectedRows,
  setSelectedContact,
  selectedContactGroup,
  setSelectedContactGroup,
  isLoadingLabels,
  selectedLabel,
  setSelectedLabel,
}: PersonalContactsLabelsProps) {
  const [openAddContactGroup, setOpenAddContactGroup] = useState(false);
  const [openEditContactGroup, setOpenEditContactGroup] = useState(false);
  const [openDeleteContactGroup, setOpenDeleteContactGroup] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(true);

  const userContactGroups = Array.isArray(contactGroups)
    ? contactGroups
        .filter((group) => group.groupType === 'USER_CONTACT_GROUP')
        .sort((a, b) => a.formattedName.localeCompare(b.formattedName))
    : [];

  const systemContactGroups = Array.isArray(contactGroups)
    ? contactGroups
        .filter((group) => group.groupType === 'SYSTEM_CONTACT_GROUP')
        .sort((a, b) => a.formattedName.localeCompare(b.formattedName))
    : [];

  return (
    <Card
      sx={{
        minWidth: 250,
        overflow: 'auto',
        height: '100%',
      }}
    >
      <Stack flexDirection="column" alignItems="center">
        <List dense>
          <ListSubheader disableGutters>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ px: 1 }}
            >
              <Typography>Labels</Typography>
              <Tooltip arrow title="Add Label">
                <span>
                  <IconButton
                    aria-label="add"
                    onClick={() => setOpenAddContactGroup(true)}
                    disabled={
                      !userView &&
                      !isAbleToSeeUIElement(
                        userRoles,
                        UserRoles.AddContactGroups.value
                      )
                    }
                    size="small"
                  >
                    <Add />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </ListSubheader>
          <ListItemButton
            onClick={() => setOpen(!open)}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ListItemIcon>
              <Google />
            </ListItemIcon>
            <ListItemText
              primary="System"
              primaryTypographyProps={{
                sx: {
                  fontWeight: 600,
                },
              }}
            />
            {!open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding dense>
              {systemContactGroups?.map((group) => {
                return (
                  <ListItem key={group.name} sx={{ py: 0, pr: 0 }}>
                    <ListItemButton
                      onClick={() => {
                        setSelectedRows([]);
                        setSelectedContact(null);
                        setSelectedLabel(group.resourceName || '');
                      }}
                      selected={selectedLabel == group.resourceName}
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      <ListItemIcon>
                        <Label />
                      </ListItemIcon>
                      <ListItemText primary={group.formattedName} />
                      <ListItemSecondaryAction>
                        <Typography variant="body2" color="textSecondary">
                          {group.memberCount}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          <ListItemButton
            onClick={() => setCustomOpen(!customOpen)}
            sx={{ width: 300 }}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary="User"
              primaryTypographyProps={{
                sx: {
                  fontWeight: 600,
                },
              }}
            />
            {!customOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={customOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding dense>
              {!isLoadingLabels ? (
                userContactGroups?.map((group, index) => {
                  return (
                    <ListItem
                      sx={{ py: 0, pr: 0 }}
                      key={group.name}
                      onMouseOver={() => setHoveredIndex(index)}
                      onMouseOut={() => setHoveredIndex(null)}
                    >
                      <ListItemButton
                        onClick={() => {
                          setSelectedRows([]);
                          setSelectedContact(null);
                          setSelectedLabel(group.resourceName || '');
                        }}
                        selected={selectedLabel == group.resourceName}
                        sx={{ borderRadius: 2 }}
                      >
                        <ListItemIcon>
                          <Label />
                        </ListItemIcon>
                        <ListItemText
                          primary={group.formattedName}
                          primaryTypographyProps={{
                            sx: {
                              width: 150,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                        <ListItemSecondaryAction>
                          {hoveredIndex === index ? (
                            <Stack
                              flexDirection="row"
                              justifyContent="flex-start"
                              gap={0.25}
                              sx={{ position: 'relative', left: 16 }}
                            >
                              <Tooltip arrow title="Edit">
                                <IconButton
                                  aria-label="edit"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedContactGroup(group);
                                    setOpenEditContactGroup(true);
                                  }}
                                  disabled={
                                    !userView &&
                                    !isAbleToSeeUIElement(
                                      userRoles,
                                      UserRoles.EditContactGroups.value
                                    )
                                  }
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip arrow title="Delete">
                                <IconButton
                                  aria-label="delete"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedContactGroup(group);
                                    setOpenDeleteContactGroup(true);
                                  }}
                                  disabled={
                                    !userView &&
                                    !isAbleToSeeUIElement(
                                      userRoles,
                                      UserRoles.DeleteContactGroups.value
                                    )
                                  }
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {group.memberCount}
                            </Typography>
                          )}
                        </ListItemSecondaryAction>
                      </ListItemButton>
                    </ListItem>
                  );
                })
              ) : (
                <Stack gap={0.5} alignItems="flex-end">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <Skeleton
                      variant="rounded"
                      height={35}
                      width={265}
                      sx={{ borderRadius: 2 }}
                      // eslint-disable-next-line react-x/no-array-index-key
                      key={index}
                    />
                  ))}
                </Stack>
              )}
            </List>
          </Collapse>
        </List>
      </Stack>
      {contactGroups ? (
        <AddContactGroupDialog
          open={openAddContactGroup}
          handleClose={() => setOpenAddContactGroup(false)}
          userEmail={userEmail}
          userView={userView}
          contactGroupList={contactGroups}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedContactGroup ? (
        <EditContactGroupDialog
          open={openEditContactGroup}
          handleClose={() => setOpenEditContactGroup(false)}
          userEmail={userEmail}
          userView={userView}
          contactGroupList={contactGroups}
          contactGroup={selectedContactGroup}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openDeleteContactGroup && selectedContactGroup ? (
        <DeleteContactGroupDialog
          open={openDeleteContactGroup}
          handleClose={() => setOpenDeleteContactGroup(false)}
          userEmail={userEmail}
          userView={userView}
          contactGroupList={contactGroups}
          contactGroup={selectedContactGroup}
          selectedLabel={selectedLabel}
          setSelectedLabel={setSelectedLabel}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </Card>
  );
}
